import { Paginated, Unit } from '../adl-gen/common';
import { DbKey, WithDbId } from '../adl-gen/common/db';
import { TableQuery } from '../adl-gen/common/tabular';
import { DeclResolver } from '../adl-gen/runtime/adl';
import { JsonBinding } from '../adl-gen/runtime/json';
import * as DB from '../adl-gen/totalinteraction/futurefit/db';
import * as LE from '../adl-gen/totalinteraction/futurefit/db/learningevent';
import * as SCODLE from '../adl-gen/totalinteraction/futurefit/db/scodlemigration';
import * as FE from '../adl-gen/totalinteraction/futurefit/frontend';
import * as LP from '../adl-gen/totalinteraction/futurefit/lpapi';
import * as R from '../adl-gen/totalinteraction/futurefit/requests';
import * as RT from '../adl-gen/totalinteraction/futurefit/requests-rtypes';
import { HttpFetch, HttpRequest } from '../hx/service/http';

import { ServiceError } from './service-error';
import { TokenManager } from './token-manager';

/**
 * The skillseeker backend service.
 */
export class Service {
  private readonly requestTypes: RT.RequestTypes;
  // private readonly lpRequestTypes: LP_RT.RequestTypes;

  /** Error handler to allow for cross cutting concerns, e.g. authorization errors */
  private errorHandler: (error: ServiceError) => void = (_error: ServiceError) => {
    // default noop placeholder
    return;
  };

  constructor(
    /** Fetcher over HTTP */
    private readonly http: HttpFetch,
    /** Base URL of the API endpoints */
    private readonly baseUrl: string,
    /** Resolver for ADL types */
    resolver: DeclResolver,
    /** Token manager storing the authentication tokens */
    public readonly tokenManager: TokenManager
  ) {
    this.requestTypes = RT.makeRequestTypes(resolver);
    // this.lpRequestTypes = LP_RT.makeRequestTypes(resolver);
  }

  async init(): Promise<void> {
    // noop
    return;
  }

  setErrorHandler(handleError: (error: ServiceError) => void) {
    this.errorHandler = handleError;
  }

  async availableLangs(query: TableQuery): Promise<Paginated<WithDbId<DB.Lang>>> {
    return this.postAdl(query, this.requestTypes.availableLangs, 'Get available langs');
  }

  async userQuizResults(userQuizId: DbKey<DB.UserQuiz>, providers: DbKey<DB.LearningProvider>[]): Promise<R.SingleUserResults> {
    return this.postAdl({ userQuizId, providers }, this.requestTypes.userQuizResults, 'userQuizResults');
  }

  async userQuizResultsForOtherRole(
    userQuizId: DbKey<DB.UserQuiz>,
    providers: DbKey<DB.LearningProvider>[],
    otherRoleId: DbKey<DB.WorkRole>): Promise<R.SingleUserResults> {
    return this.postAdl({
      userQuizId,
      providers,
      otherRoleId
    },
      this.requestTypes.userQuizResultsForRole,
      'userQuizResults'
    );
  }

  async queryComparisonRoles(): Promise<FE.ComparisonRoles> {
    return this.postAdl({}, this.requestTypes.queryComparisonRoles, 'query roles for Role Comparison');
  }

  /// Uses Auth0 token not Server token
  async userRegisterLogin(): Promise<R.UserLoginResp> {
    return this.postAdl({}, this.requestTypes.userLogin, 'user register/login');
  }

  /// Uses Auth0 token not Server token
  async checkAuth(): Promise<DbKey<DB.AppUser> | null> {
    return this.getAdl(this.requestTypes.checkAuth, 'check auth on backend');
  }

  async createAppClient(appClient: DB.AppClient): Promise<DbKey<DB.AppClient>> {
    return this.postAdl(appClient, this.requestTypes.createAppClient, 'create appClient');
  }

  async updateAppClient(appClient: WithDbId<DB.AppClient>): Promise<void> {
    await this.postAdl(appClient, this.requestTypes.updateAppClient, 'update appClient');
  }

  async deleteAppClient(appClient: DbKey<DB.AppClient>): Promise<void> {
    await this.postAdl(appClient, this.requestTypes.deleteAppClient, 'delete appClient');
  }

  async queryAppClients(query: TableQuery): Promise<Paginated<WithDbId<DB.AppClient>>> {
    return this.postAdl(query, this.requestTypes.queryAppClients, 'query for appClients');
  }

  async createUser(user: DB.AppUser): Promise<DbKey<DB.AppUser>> {
    return this.postAdl(user, this.requestTypes.createUser, 'create user');
  }

  async updateUser(user: WithDbId<DB.AppUser>): Promise<void> {
    await this.postAdl(user, this.requestTypes.updateUser, 'update user');
  }

  async deleteUser(user: DbKey<DB.AppUser>): Promise<void> {
    await this.postAdl(user, this.requestTypes.deleteUser, 'delete user');
  }

  async queryUsers(query: TableQuery): Promise<Paginated<WithDbId<DB.AppUser>>> {
    return this.postAdl(query, this.requestTypes.queryUsers, 'query for users');
  }

  async createLicence(Licence: DB.Licence): Promise<DbKey<DB.Licence>> {
    return this.postAdl(Licence, this.requestTypes.createLicence, 'create Licence');
  }

  async updateLicence(Licence: WithDbId<DB.Licence>): Promise<void> {
    await this.postAdl(Licence, this.requestTypes.updateLicence, 'update Licence');
  }

  async deleteLicence(Licence: DbKey<DB.Licence>): Promise<void> {
    await this.postAdl(Licence, this.requestTypes.deleteLicence, 'delete Licence');
  }

  async queryLicences(query: TableQuery): Promise<Paginated<WithDbId<DB.Licence>>> {
    return this.postAdl(query, this.requestTypes.queryLicences, 'query for Licences');
  }

  async createAppClientLicence(appClientLicence: DB.AppClientLicence): Promise<DbKey<DB.AppClientLicence>> {
    return this.postAdl(appClientLicence, this.requestTypes.createAppClientLicence, 'create appClientLicence');
  }

  async updateAppClientLicence(appClientLicence: WithDbId<DB.AppClientLicence>): Promise<void> {
    await this.postAdl(appClientLicence, this.requestTypes.updateAppClientLicence, 'update appClientLicence');
  }

  async deleteAppClientLicence(appClientLicence: DbKey<DB.AppClientLicence>): Promise<void> {
    await this.postAdl(appClientLicence, this.requestTypes.deleteAppClientLicence, 'delete appClientLicence');
  }

  async queryAppClientLicences(query: TableQuery): Promise<Paginated<WithDbId<DB.AppClientLicence>>> {
    return this.postAdl(query, this.requestTypes.queryAppClientLicences, 'query for appClientLicences');
  }

  async createCompetency(competency: DB.Competency): Promise<DbKey<DB.Competency>> {
    return this.postAdl(competency, this.requestTypes.createCompetency, 'create competency');
  }

  async updateCompetency(competency: WithDbId<DB.Competency>): Promise<void> {
    await this.postAdl(competency, this.requestTypes.updateCompetency, 'update competency');
  }

  async deleteCompetency(competency: DbKey<DB.Competency>): Promise<void> {
    await this.postAdl(competency, this.requestTypes.deleteCompetency, 'delete competency');
  }

  async queryCompetencies(query: TableQuery): Promise<Paginated<WithDbId<DB.Competency>>> {
    return this.postAdl(query, this.requestTypes.queryCompetencies, 'query for competencies');
  }

  async createSkill(skill: DB.Skill): Promise<DbKey<DB.Skill>> {
    return this.postAdl(skill, this.requestTypes.createSkill, 'create skill');
  }

  async updateSkill(skill: WithDbId<DB.Skill>): Promise<void> {
    await this.postAdl(skill, this.requestTypes.updateSkill, 'update skill');
  }

  async deleteSkill(skill: DbKey<DB.Skill>): Promise<void> {
    await this.postAdl(skill, this.requestTypes.deleteSkill, 'delete skill');
  }

  async querySkills(query: TableQuery): Promise<Paginated<WithDbId<DB.Skill>>> {
    return this.postAdl(query, this.requestTypes.querySkills, 'query for skills');
  }

  async createWorkRole(workrole: DB.WorkRole): Promise<DbKey<DB.WorkRole>> {
    return this.postAdl(workrole, this.requestTypes.createWorkRole, 'create workrole');
  }

  async updateWorkRole(workrole: WithDbId<DB.WorkRole>): Promise<void> {
    await this.postAdl(workrole, this.requestTypes.updateWorkRole, 'update workrole');
  }

  async deleteWorkRole(workrole: DbKey<DB.WorkRole>): Promise<void> {
    await this.postAdl(workrole, this.requestTypes.deleteWorkRole, 'delete workrole');
  }

  async queryWorkRoles(query: TableQuery): Promise<Paginated<WithDbId<DB.WorkRole>>> {
    return this.postAdl(query, this.requestTypes.queryWorkRoles, 'query for workroles');
  }

  async createWorkRoleSkill(workroleskill: DB.WorkRoleSkill): Promise<DbKey<DB.WorkRoleSkill>> {
    return this.postAdl(workroleskill, this.requestTypes.createWorkRoleSkill, 'create workroleskill');
  }

  async updateWorkRoleSkill(workroleskill: WithDbId<DB.WorkRoleSkill>): Promise<void> {
    await this.postAdl(workroleskill, this.requestTypes.updateWorkRoleSkill, 'update workroleskill');
  }

  async deleteWorkRoleSkill(workroleskill: DbKey<DB.WorkRoleSkill>): Promise<void> {
    await this.postAdl(workroleskill, this.requestTypes.deleteWorkRoleSkill, 'delete workroleskill');
  }

  async queryWorkRoleSkills(query: TableQuery): Promise<Paginated<WithDbId<DB.WorkRoleSkill>>> {
    return this.postAdl(query, this.requestTypes.queryWorkRoleSkills, 'query for workroleskills');
  }

  async createQuestion(question: DB.Question): Promise<DbKey<DB.Question>> {
    return this.postAdl(question, this.requestTypes.createQuestion, 'create question');
  }

  async updateQuestion(question: WithDbId<DB.Question>): Promise<void> {
    await this.postAdl(question, this.requestTypes.updateQuestion, 'update question');
  }

  async deleteQuestion(question: DbKey<DB.Question>): Promise<void> {
    await this.postAdl(question, this.requestTypes.deleteQuestion, 'delete question');
  }

  async queryQuestions(query: TableQuery): Promise<Paginated<WithDbId<DB.Question>>> {
    return this.postAdl(query, this.requestTypes.queryQuestions, 'query for questions');
  }

  async createQuiz(quizReq: R.CreateQuizReq): Promise<DbKey<DB.Quiz>> {
    return this.postAdl(quizReq, this.requestTypes.createQuiz, 'create quiz');
  }

  async updateQuiz(quizReq: R.UpdateQuizReq): Promise<void> {
    await this.postAdl(quizReq, this.requestTypes.updateQuiz, 'update quiz');
  }

  async queryQuizzes(query: TableQuery): Promise<Paginated<WithDbId<DB.Quiz>>> {
    return this.postAdl(query, this.requestTypes.queryQuizzes, 'query for quizzes');
  }

  async queryQuizReports(appUserId: DbKey<DB.AppUser>): Promise<Paginated<R.QuizReportSummary>> {
    return this.postAdl(appUserId, this.requestTypes.queryQuizReports, 'query for quiz reports');
  }

  async queryQuizReportParticipants(
    viewerAppUserId: DbKey<DB.AppUser>,
    quizId: DbKey<DB.Quiz>
  ): Promise<FE.UsersGroupsMap> {
    return this.postAdl(
      { viewerAppUserId, quizId },
      this.requestTypes.queryQuizReportParticipants,
      'query participants in a report'
    );
  }

  // the newer MultiUserQuizResults
  async queryQuizReportMulti(
    viewerAppUserId: DbKey<DB.AppUser>,
    quizId: DbKey<DB.Quiz>,
    includedAppUserIds: DbKey<DB.AppUser>[]
  ): Promise<FE.MultiUserQuizResults> {
    return this.postAdl(
      { viewerAppUserId, quizId, includedAppUserIds },
      this.requestTypes.queryQuizReportMulti,
      'query results for a report'
    );
  }

  // Skill and Will
  async queryQuizReportRaw(
    appClientId: DbKey<DB.AppClient>,
    viewerAppUserId?: DbKey<DB.AppUser>,
    quizId?: DbKey<DB.Quiz>,
    includedUserIds?: DbKey<DB.AppUser>[]
  ): Promise<R.TempMultiUserQuizResultsRaw> {
    return this.postAdl(
      {
        appClientId,
        viewerAppUserId: viewerAppUserId || null,
        quizId: quizId || null,
        includedUserIds: includedUserIds || []
      },
      this.requestTypes.queryQuizReportRaw,
      'query results for Skill and Will report'
    );
  }

  // AusPost User Report
  async queryUserAttemptHistory(appClientId: DbKey<DB.AppClient>): Promise<R.UserAttemptHistoryRaw> {
    return this.postAdl(
      { appClientId },
      this.requestTypes.queryUserAttemptHistory,
      'query user attempt history for AusPost report'
    );
  }

  async queryGroupResultsScoreSet(
    appClientId: DbKey<DB.AppClient>,
    quizId: DbKey<DB.Quiz>
  ): Promise<FE.ResultsScoreSet> {
    return this.postAdl(
      { appClientId, quizId },
      this.requestTypes.queryGroupResultsScoreSet,
      'query group results for client and quiz'
    );
  }

  async updateQuizSkill(quizSkill: WithDbId<DB.QuizSkill>): Promise<void> {
    await this.postAdl(quizSkill, this.requestTypes.updateQuizSkill, 'update quiz skill');
  }

  async queryQuizSkills(query: TableQuery): Promise<Paginated<WithDbId<DB.QuizSkill>>> {
    return this.postAdl(query, this.requestTypes.queryQuizSkills, 'query for quiz skills');
  }

  async updateQuizQuestion(quizQuestion: WithDbId<DB.QuizQuestion>): Promise<void> {
    await this.postAdl(quizQuestion, this.requestTypes.updateQuizQuestion, 'update quiz question');
  }

  async queryQuizQuestions(query: TableQuery): Promise<Paginated<WithDbId<DB.QuizQuestion>>> {
    return this.postAdl(query, this.requestTypes.queryQuizQuestions, 'query for quiz questions');
  }

  async createUserGroup(usergroup: DB.UserGroup): Promise<DbKey<DB.UserGroup>> {
    return this.postAdl(usergroup, this.requestTypes.createUserGroup, 'create usergroup');
  }

  async updateUserGroup(usergroup: WithDbId<DB.UserGroup>): Promise<void> {
    await this.postAdl(usergroup, this.requestTypes.updateUserGroup, 'update usergroup');
  }

  async deleteUserGroup(usergroup: DbKey<DB.UserGroup>): Promise<void> {
    await this.postAdl(usergroup, this.requestTypes.deleteUserGroup, 'delete usergroup');
  }

  async queryUserGroups(query: TableQuery): Promise<Paginated<WithDbId<DB.UserGroup>>> {
    return this.postAdl(query, this.requestTypes.queryUserGroups, 'query for usergroups');
  }

  async createUserGroupMember(usergroupmember: DB.UserGroupMember): Promise<DbKey<DB.UserGroupMember>> {
    return this.postAdl(usergroupmember, this.requestTypes.createUserGroupMember, 'create usergroupmember');
  }

  async updateUserGroupMember(usergroupmember: WithDbId<DB.UserGroupMember>): Promise<void> {
    await this.postAdl(usergroupmember, this.requestTypes.updateUserGroupMember, 'update usergroupmember');
  }

  async deleteUserGroupMember(usergroupmember: DbKey<DB.UserGroupMember>): Promise<void> {
    await this.postAdl(usergroupmember, this.requestTypes.deleteUserGroupMember, 'delete usergroupmember');
  }

  async queryUserGroupMembers(query: TableQuery): Promise<Paginated<WithDbId<DB.UserGroupMember>>> {
    return this.postAdl(query, this.requestTypes.queryUserGroupMembers, 'query for usergroupmembers');
  }

  async createUserGroupManager(usergroupmanager: DB.UserGroupManager): Promise<DbKey<DB.UserGroupManager>> {
    return this.postAdl(usergroupmanager, this.requestTypes.createUserGroupManager, 'create usergroupmanager');
  }

  async updateUserGroupManager(usergroupmanager: WithDbId<DB.UserGroupManager>): Promise<void> {
    await this.postAdl(usergroupmanager, this.requestTypes.updateUserGroupManager, 'update usergroupmanager');
  }

  async deleteUserGroupManager(usergroupmanager: DbKey<DB.UserGroupManager>): Promise<void> {
    await this.postAdl(usergroupmanager, this.requestTypes.deleteUserGroupManager, 'delete usergroupmanager');
  }

  async queryUserGroupManagers(query: TableQuery): Promise<Paginated<WithDbId<DB.UserGroupManager>>> {
    return this.postAdl(query, this.requestTypes.queryUserGroupManagers, 'query for usergroupmanagers');
  }

  async createUserQuiz(userQuiz: DB.UserQuiz): Promise<DbKey<DB.UserQuiz>> {
    return this.postAdl(userQuiz, this.requestTypes.createUserQuiz, 'create userQuiz');
  }

  async updateUserQuiz(userQuiz: WithDbId<DB.UserQuiz>): Promise<void> {
    await this.postAdl(userQuiz, this.requestTypes.updateUserQuiz, 'update userQuiz');
  }

  async deleteUserQuiz(userQuiz: DbKey<DB.UserQuiz>): Promise<void> {
    await this.postAdl(userQuiz, this.requestTypes.deleteUserQuiz, 'delete userQuiz');
  }

  async queryUserQuizs(query: TableQuery): Promise<Paginated<WithDbId<DB.UserQuiz>>> {
    return this.postAdl(query, this.requestTypes.queryUserQuizs, 'query for userQuizs');
  }

  async createUserUploadRecord(userUpload: DB.UserUploadRecord): Promise<DbKey<DB.UserUploadRecord>> {
    return this.postAdl(userUpload, this.requestTypes.createUserUploadRecord, 'create userUpload');
  }

  async updateUserUploadRecord(userUpload: WithDbId<DB.UserUploadRecord>): Promise<void> {
    await this.postAdl(userUpload, this.requestTypes.updateUserUploadRecord, 'update userUpload');
  }

  async deleteUserUploadRecord(userUpload: DbKey<DB.UserUploadRecord>): Promise<void> {
    await this.postAdl(userUpload, this.requestTypes.deleteUserUploadRecord, 'delete userUpload');
  }

  async queryUserUploadRecords(query: TableQuery): Promise<Paginated<WithDbId<DB.UserUploadRecord>>> {
    return this.postAdl(query, this.requestTypes.queryUserUploadRecords, 'query for userUploads');
  }

  async createUserUploadSet(userUploadSet: DB.UserUploadSet): Promise<DbKey<DB.UserUploadSet>> {
    return this.postAdl(userUploadSet, this.requestTypes.createUserUploadSet, 'create userUploadSet');
  }

  async updateUserUploadSet(userUploadSet: WithDbId<DB.UserUploadSet>): Promise<void> {
    await this.postAdl(userUploadSet, this.requestTypes.updateUserUploadSet, 'update userUploadSet');
  }

  async deleteUserUploadSet(userUploadSet: DbKey<DB.UserUploadSet>): Promise<void> {
    await this.postAdl(userUploadSet, this.requestTypes.deleteUserUploadSet, 'delete userUploadSet');
  }

  async queryUserUploadSets(query: TableQuery): Promise<Paginated<WithDbId<DB.UserUploadSet>>> {
    return this.postAdl(query, this.requestTypes.queryUserUploadSets, 'query for userUploadSets');
  }

  async processUserUpload(upload: R.UserUploadData): Promise<R.UserUploadResult> {
    return this.postAdl(upload, this.requestTypes.processUserUpload, 'Process User Upload');
  }

  /// FF2-355
  async recreateUserQuiz(userQuiz: DB.UserQuiz): Promise<DbKey<DB.UserQuiz>> {
    return this.postAdl(userQuiz, this.requestTypes.recreateUserQuiz, 'recreate userQuiz');
  }

  async queryQuizzesForUser(userId: DbKey<DB.AppUser>): Promise<Paginated<FE.UserQuizSummary>> {
    return this.postAdl(userId, this.requestTypes.queryQuizzesForUser, 'query quizzes for user');
  }

  async getNextQuestion(userQuizId: DbKey<DB.UserQuiz>): Promise<R.NextQuestionResp> {
    return this.postAdl(userQuizId, this.requestTypes.getNextQuestion, 'get the next quiz qustion for a user');
  }

  async userQuizWorkRole(userQuizId: DbKey<DB.UserQuiz>): Promise<FE.WorkRoleListEntry> {
    return this.postAdl(
      userQuizId,
      this.requestTypes.getWorkRoleForUserQuiz,
      'get the WorkRoleListEntry for the UserQuiz'
    );
  }

  async completeUserQuiz(userQuizId: DbKey<DB.UserQuiz>): Promise<Unit> {
    return this.postAdl(userQuizId, this.requestTypes.completeUserQuiz, 'mark self-assessment completed');
  }

  async quizAnswerQuestion(req: R.QuizAnswerQuestionReq): Promise<Unit> {
    return this.postAdl(req, this.requestTypes.quizAnswerQuestion, 'submit an answer for a question');
  }

  async selfAssessmentAnswerQuestion(req: R.SelfAssessmentAnswerQuestionReq): Promise<Unit> {
    return this.postAdl(
      req,
      this.requestTypes.selfAssessmentAnswerQuestion,
      "submit an answer for a self-assessment 'question'"
    );
  }

  async quizQuestionEvent(req: R.QuizQuestionEventReq): Promise<Unit> {
    return this.postAdl(req, this.requestTypes.quizQuestionEvent, 'submit an event whilst answering a question');
  }

  async queryClientParticipantsReport(appClientId: DbKey<DB.AppClient>): Promise<Paginated<FE.ClientParticipant>> {
    return this.postAdl(appClientId, this.requestTypes.clientParticipantsReport, 'REPORT: Client Participants');
  }

  async querySkillTrainingOfferingMatrix(): Promise<FE.SkillTrainingOfferingMatrix> {
    return this.postAdl({}, this.requestTypes.skillTrainingOfferingReport, 'REPORT: SkillTrainingOffering matrix');
  }

  async createLearningProvider(learningProvider: DB.LearningProvider): Promise<DbKey<DB.LearningProvider>> {
    return this.postAdl(learningProvider, this.requestTypes.createLearningProvider, 'create learningProvider');
  }

  async updateLearningProvider(learningProvider: WithDbId<DB.LearningProvider>): Promise<void> {
    await this.postAdl(learningProvider, this.requestTypes.updateLearningProvider, 'update learningProvider');
  }

  async deleteLearningProvider(learningProvider: DbKey<DB.LearningProvider>): Promise<void> {
    await this.postAdl(learningProvider, this.requestTypes.deleteLearningProvider, 'delete learningProvider');
  }

  async queryLearningProviders(query: TableQuery): Promise<Paginated<WithDbId<DB.LearningProvider>>> {
    return this.postAdl(query, this.requestTypes.queryLearningProviders, 'query for learningProviders');
  }

  async createLearningProviderCatalogEntry(
    learningProviderCatalogEntry: DB.LearningProviderCatalogEntry
  ): Promise<DbKey<DB.LearningProviderCatalogEntry>> {
    return this.postAdl(
      learningProviderCatalogEntry,
      this.requestTypes.createLearningProviderCatalogEntry,
      'create learningProviderCatalogEntry'
    );
  }

  async updateLearningProviderCatalogEntry(
    learningProviderCatalogEntry: WithDbId<DB.LearningProviderCatalogEntry>
  ): Promise<void> {
    await this.postAdl(
      learningProviderCatalogEntry,
      this.requestTypes.updateLearningProviderCatalogEntry,
      'update learningProviderCatalogEntry'
    );
  }

  async deleteLearningProviderCatalogEntry(
    learningProviderCatalogEntry: DbKey<DB.LearningProviderCatalogEntry>
  ): Promise<void> {
    await this.postAdl(
      learningProviderCatalogEntry,
      this.requestTypes.deleteLearningProviderCatalogEntry,
      'delete learningProviderCatalogEntry'
    );
  }

  async queryLearningProviderCatalogEntries(
    query: TableQuery
  ): Promise<Paginated<WithDbId<DB.LearningProviderCatalogEntry>>> {
    return this.postAdl(
      query,
      this.requestTypes.queryLearningProviderCatalogEntrys,
      'query for learningProviderCatalogEntries'
    );
  }

  async queryLearningProviderCatalog(learningProvider: DbKey<DB.LearningProvider>): Promise<LP.CatalogResp> {
    return this.postAdl(
      learningProvider,
      this.requestTypes.queryLearningProviderCatalog,
      'Get fresh copy of LP Catalog from LP'
    );
  }

  async createTrainingOffering(trainingOffering: DB.TrainingOffering): Promise<DbKey<DB.TrainingOffering>> {
    return this.postAdl(trainingOffering, this.requestTypes.createTrainingOffering, 'create trainingOffering');
  }

  async updateTrainingOffering(trainingOffering: WithDbId<DB.TrainingOffering>): Promise<void> {
    await this.postAdl(trainingOffering, this.requestTypes.updateTrainingOffering, 'update trainingOffering');
  }

  async deleteTrainingOffering(trainingOffering: DbKey<DB.TrainingOffering>): Promise<void> {
    await this.postAdl(trainingOffering, this.requestTypes.deleteTrainingOffering, 'delete trainingOffering');
  }

  async queryTrainingOfferings(query: TableQuery): Promise<Paginated<WithDbId<DB.TrainingOffering>>> {
    return this.postAdl(query, this.requestTypes.queryTrainingOfferings, 'query for trainingOfferings');
  }

  async createUserTrainingOffering(
    userTrainingOffering: DB.UserTrainingOffering
  ): Promise<DbKey<DB.UserTrainingOffering>> {
    return this.postAdl(
      userTrainingOffering,
      this.requestTypes.createUserTrainingOffering,
      'create userTrainingOffering'
    );
  }

  async updateUserTrainingOffering(userTrainingOffering: WithDbId<DB.UserTrainingOffering>): Promise<void> {
    await this.postAdl(
      userTrainingOffering,
      this.requestTypes.updateUserTrainingOffering,
      'update userTrainingOffering'
    );
  }

  async deleteUserTrainingOffering(userTrainingOffering: DbKey<DB.UserTrainingOffering>): Promise<void> {
    await this.postAdl(
      userTrainingOffering,
      this.requestTypes.deleteUserTrainingOffering,
      'delete userTrainingOffering'
    );
  }

  async queryUserTrainingOfferings(query: TableQuery): Promise<Paginated<WithDbId<DB.UserTrainingOffering>>> {
    return this.postAdl(query, this.requestTypes.queryUserTrainingOfferings, 'query for userTrainingOfferings');
  }

  async queryQuizTrainingRecommendations(
    viewerAppUserId: DbKey<DB.AppUser>,
    quizId: DbKey<DB.Quiz>,
    recommendationMethod?: DB.RoleRecommendationMethod,
    recommendationProviders?: DbKey<DB.LearningProvider>[]
  ): Promise<FE.MultiUserTrainingRecommendations> {
    const method = recommendationMethod || DB.RoleRecommendationMethod.relevance;
    const providers = recommendationProviders || [];
    return this.postAdl(
      { viewerAppUserId, quizId, recommendationMethod: method, recommendationProviders: providers },
      this.requestTypes.queryQuizTrainingRecommendations,
      'query Quiz training recommendations (for manager)'
    );
  }

  async createSkillTrainingOffering(
    skillTrainingOffering: DB.SkillTrainingOffering
  ): Promise<DbKey<DB.SkillTrainingOffering>> {
    return this.postAdl(
      skillTrainingOffering,
      this.requestTypes.createSkillTrainingOffering,
      'create SkillTrainingOffering'
    );
  }

  async updateSkillTrainingOffering(skillTrainingOffering: WithDbId<DB.SkillTrainingOffering>): Promise<void> {
    await this.postAdl(
      skillTrainingOffering,
      this.requestTypes.updateSkillTrainingOffering,
      'update SkillTrainingOffering'
    );
  }

  async deleteSkillTrainingOffering(skillTrainingOffering: DbKey<DB.SkillTrainingOffering>): Promise<void> {
    await this.postAdl(
      skillTrainingOffering,
      this.requestTypes.deleteSkillTrainingOffering,
      'delete SkillTrainingOffering'
    );
  }

  async createCompetencyTrainingOffering(
    competencyTrainingOffering: DB.CompetencyTrainingOffering
  ): Promise<DbKey<DB.CompetencyTrainingOffering>> {
    return this.postAdl(
      competencyTrainingOffering,
      this.requestTypes.createCompetencyTrainingOffering,
      'create CompetencyTrainingOffering'
    );
  }

  async updateCompetencyTrainingOffering(
    competencyTrainingOffering: WithDbId<DB.CompetencyTrainingOffering>
  ): Promise<void> {
    await this.postAdl(
      competencyTrainingOffering,
      this.requestTypes.updateCompetencyTrainingOffering,
      'update CompetencyTrainingOffering'
    );
  }

  async deleteCompetencyTrainingOffering(
    competencyTrainingOffering: DbKey<DB.CompetencyTrainingOffering>
  ): Promise<void> {
    await this.postAdl(
      competencyTrainingOffering,
      this.requestTypes.deleteCompetencyTrainingOffering,
      'delete CompetencyTrainingOffering'
    );
  }

  async createScodleControl(scodleControl: SCODLE.ScodleControl): Promise<DbKey<SCODLE.ScodleControl>> {
    return this.postAdl(scodleControl, this.requestTypes.createScodleControl, 'create scodleControl');
  }

  async updateScodleControl(scodleControl: WithDbId<SCODLE.ScodleControl>): Promise<void> {
    await this.postAdl(scodleControl, this.requestTypes.updateScodleControl, 'update scodleControl');
  }

  async deleteScodleControl(scodleControl: DbKey<SCODLE.ScodleControl>): Promise<void> {
    await this.postAdl(scodleControl, this.requestTypes.deleteScodleControl, 'delete scodleControl');
  }

  async queryScodleControls(query: TableQuery): Promise<Paginated<WithDbId<SCODLE.ScodleControl>>> {
    return this.postAdl(query, this.requestTypes.queryScodleControls, 'query for report');
  }

  async createReport(report: DB.Report): Promise<DbKey<DB.Report>> {
    return this.postAdl(report, this.requestTypes.createReport, 'create report');
  }

  async updateReport(report: WithDbId<DB.Report>): Promise<void> {
    await this.postAdl(report, this.requestTypes.updateReport, 'update report');
  }

  async deleteReport(report: DbKey<DB.Report>): Promise<void> {
    await this.postAdl(report, this.requestTypes.deleteReport, 'delete report');
  }

  async queryReports(query: TableQuery): Promise<Paginated<WithDbId<DB.Report>>> {
    return this.postAdl(query, this.requestTypes.queryReports, 'query for reports');
  }

  async queryScodleEvents(query: TableQuery): Promise<Paginated<WithDbId<LE.ScodleEventLog>>> {
    return this.postAdl(query, this.requestTypes.queryScodleEvents, 'query for Scodle events');
  }

  async createUserWithGroup(user: DB.AppUser): Promise<DbKey<DB.AppUser>> {
    return this.postAdl(
      user,
      this.requestTypes.createUserWithGroup,
      'create user and add to global UserGroup if exists'
    );
  }

  async createAppClientWithGroup(appClient: DB.AppClient): Promise<DbKey<DB.AppClient>> {
    return this.postAdl(appClient, this.requestTypes.createAppClientWithGroup, 'create appClient and global UserGroup');
  }

  async queryAppClientList(query: TableQuery): Promise<Paginated<FE.AppClientListEntry>> {
    return this.postAdl(query, this.requestTypes.queryAppClientList, 'List AppClients');
  }

  async queryAppUserList(appClientId: DbKey<DB.AppClient>): Promise<Paginated<FE.AppUserListEntry>> {
    return this.postAdl(appClientId, this.requestTypes.queryAppUserList, 'List AppUsers for client');
  }

  async queryAppUserData(appClientId: DbKey<DB.AppClient>): Promise<R.UserData> {
    return this.postAdl(appClientId, this.requestTypes.queryAppUserData, 'Query lookup data for users');
  }

  async queryUserGroupList(appClientId: DbKey<DB.AppClient>): Promise<Paginated<FE.UserGroupListEntry>> {
    return this.postAdl(appClientId, this.requestTypes.queryUserGroupList, 'List UserGroups for client');
  }

  async queryUserGroupData(appClientId: DbKey<DB.AppClient>): Promise<R.GroupData> {
    return this.postAdl(appClientId, this.requestTypes.queryUserGroupData, 'Query lookup data for UserGroups');
  }

  async queryWorkRoleList(query: TableQuery): Promise<Paginated<FE.WorkRoleListEntry>> {
    return this.postAdl(query, this.requestTypes.queryWorkRoleList, 'Query WorkRoles for client');
  }

  async queryWorkRoleReferenceData(): Promise<R.WorkRoleReferenceData> {
    return this.getAdl(this.requestTypes.queryWorkRoleReferenceData, 'Query lookup data for WorkRoles');
  }

  async createWorkRoleWithRanges(req: R.CreateWorkRoleReq): Promise<DbKey<DB.WorkRole>> {
    return this.postAdl(req, this.requestTypes.createWorkRoleWithRanges, 'Create WorkRole with Ranges');
  }

  async updateWorkRoleWithRanges(req: R.UpdateWorkRoleReq): Promise<void> {
    await this.postAdl(req, this.requestTypes.updateWorkRoleWithRanges, 'Update WorkRole and Ranges');
  }

  async deleteWorkRoleWithRanges(req: DbKey<DB.WorkRole>): Promise<void> {
    await this.postAdl(req, this.requestTypes.deleteWorkRoleWithRanges, 'Delete WorkRole, Ranges and linked skills');
  }

  async cloneWorkRole(req: R.CloneWorkRoleReq): Promise<DbKey<DB.WorkRole>> {
    return this.postAdl(req, this.requestTypes.cloneWorkRole, 'Clone WorkRole with Ranges and copy linked skills');
  }

  async queryRoleBuilderData(roleId: DbKey<DB.WorkRole>): Promise<FE.RoleBuilderData> {
    return this.postAdl(roleId, this.requestTypes.queryRoleBuilderData, 'Fetch RoleBuilder data for role');
  }

  async updateRoleBuilderData(data: FE.RoleBuilderUpdates): Promise<void> {
    await this.postAdl(data, this.requestTypes.updateRoleBuilderData, 'Update RoleBuillder data');
  }

  async querySkillData(req: R.QuerySkillDataReq): Promise<FE.SkillData> {
    return this.postAdl(
      req,
      this.requestTypes.querySkillData,
      'Fetch lookup data for Skill list'
    );
  }

  async queryTrainingOfferingData(req: R.QueryTrainingOfferingDataReq): Promise<FE.TrainingOfferingData> {
    return this.postAdl(
      req,
      this.requestTypes.queryTrainingOfferingData,
      'Fetch lookup data for TrainingOffering list'
    );
  }

  async createAuth0Tenant(tenant: DB.Auth0Tenant): Promise<DbKey<DB.Auth0Tenant>> {
    return this.postAdl(tenant, this.requestTypes.createAuth0Tenant, 'Create Auth0Tenant');
  }

  async updateAuth0Tenant(tenant: WithDbId<DB.Auth0Tenant>): Promise<void> {
    await this.postAdl(tenant, this.requestTypes.updateAuth0Tenant, 'Update Auth0Tenant');
  }

  async deleteAuth0Tenant(tenantId: DbKey<DB.Auth0Tenant>): Promise<void> {
    await this.postAdl(tenantId, this.requestTypes.deleteAuth0Tenant, 'Delete Auth0Tenant');
  }

  async queryAuth0Tenants(q: TableQuery): Promise<Paginated<WithDbId<DB.Auth0Tenant>>> {
    return this.postAdl(q, this.requestTypes.queryAuth0Tenants, 'Query Auth0Tenants');
  }

  async createAuth0Client(req: R.CreateAuth0ClientReq): Promise<FE.Auth0Client> {
    return this.postAdl(req, this.requestTypes.createAuth0Client, "Create Auth0 'Client'");
  }

  async updateAuth0Client(req: R.UpdateAuth0ClientReq): Promise<FE.Auth0Client> {
    return this.postAdl(req, this.requestTypes.updateAuth0Client, "Update Auth0 'Client'");
  }

  async createAuth0SamlConnection(tenant: DB.Auth0SamlConnection): Promise<DbKey<DB.Auth0SamlConnection>> {
    return this.postAdl(tenant, this.requestTypes.createAuth0SamlConnection, 'Create Auth0SamlConnection');
  }

  async updateAuth0SamlConnection(tenant: WithDbId<DB.Auth0SamlConnection>): Promise<void> {
    await this.postAdl(tenant, this.requestTypes.updateAuth0SamlConnection, 'Update Auth0SamlConnection');
  }

  async deleteAuth0SamlConnection(tenantId: DbKey<DB.Auth0SamlConnection>): Promise<void> {
    await this.postAdl(tenantId, this.requestTypes.deleteAuth0SamlConnection, 'Delete Auth0SamlConnection');
  }

  async queryAuth0SamlConnections(q: TableQuery): Promise<Paginated<WithDbId<DB.Auth0SamlConnection>>> {
    return this.postAdl(q, this.requestTypes.queryAuth0SamlConnections, 'Query Auth0SamlConnections');
  }

  async createWorkRoleScoreRange(range: DB.WorkRoleScoreRange): Promise<DbKey<DB.WorkRoleScoreRange>> {
    return this.postAdl(range, this.requestTypes.createWorkRoleScoreRange, 'Create WorkRoleScoreRange');
  }

  async updateWorkRoleScoreRange(range: WithDbId<DB.WorkRoleScoreRange>): Promise<void> {
    await this.postAdl(range, this.requestTypes.updateWorkRoleScoreRange, 'Update WorkRoleScoreRange');
  }

  async deleteWorkRoleScoreRange(rangeId: DbKey<DB.WorkRoleScoreRange>): Promise<void> {
    await this.postAdl(rangeId, this.requestTypes.deleteWorkRoleScoreRange, 'Delete WorkRoleScoreRange');
  }

  async queryWorkRoleScoreRanges(q: TableQuery): Promise<Paginated<WithDbId<DB.WorkRoleScoreRange>>> {
    return this.postAdl(q, this.requestTypes.queryWorkRoleScoreRanges, 'Query WorkRoleScoreRanges');
  }

  async listRepositoryEntries(req: R.RepositoryReq): Promise<FE.RepositoryEntries> {
    return this.postAdl(req, this.requestTypes.listRepositoryEntries, 'List Repository Entries');
  }

  async deleteRepositoryEntry(req: R.RepositoryReq): Promise<void> {
    await this.postAdl(req, this.requestTypes.deleteRepositoryEntry, 'Delete Repository Entry');
  }

  async uploadRepositoryEntry(req: R.RepositoryPutReq): Promise<FE.Entry> {
    return this.postAdl(req, this.requestTypes.uploadRepositoryEntry, 'Upload repository entry');
  }

  async purgeUserQuiz(req: DbKey<DB.UserQuiz>): Promise<void> {
    await this.postAdl(req, this.requestTypes.purgeUserQuiz, 'Purge User Quiz and associated items');
  }

  /// Learning Events
  async generateViewEvent(req: LE.ViewEvent): Promise<FE.InputEvent> {
    return this.postAdl(req, this.requestTypes.generateViewEvent, 'Encrypt View learning event');
  }
  async generateLoginEvent(req: LE.LoginEvent): Promise<FE.InputEvent> {
    return this.postAdl(req, this.requestTypes.generateLoginEvent, 'Encrypt Login learning event');
  }
  async generateCompletionEvent(req: LE.CompletionEvent): Promise<FE.InputEvent> {
    return this.postAdl(req, this.requestTypes.generateCompletionEvent, 'Encrypt Completion learning event');
  }

  async saveSyntheticCompletionEvent(req: LE.CompletionEvent): Promise<DbKey<LE.ScodleEventLog>> {
    return this.postAdl(req, this.requestTypes.saveSyntheticCompletionEvent, 'Mock save Completion event');
  }

  async readLearningEvent(req: FE.InputEvent): Promise<LE.LearningEvent> {
    return this.getAdlWithPayload(req, this.requestTypes.readEvent, 'Decrypt and return learning event');
  }

  async captureLearningEvent(req: FE.InputEvent): Promise<void> {
    await this.getAdlWithPayload(req, this.requestTypes.captureEvent, 'Decrypt and save learning event');
  }

  captureLearningEventUrl(req: FE.InputEvent): string {
    // Does not actually call the API, instead it returns the URL. For use in Markdown contexts and other places where
    // a DOM link is directly clicked.
    // No auth headers provided
    return this.getPathAndSearch(req, this.requestTypes.captureEvent);
  }

  /// INFO: Used in integration testing only (so far)
  async launchTrainingOffering(
    trainingOfferingId: DbKey<DB.TrainingOffering>,
    appUserId: DbKey<DB.AppUser>
  ): Promise<void> {
    // This does not use a query string - the URL contains the payload (/training-offering/launch/:trainingOfferingId/:appUserId)
    // Also no return value - it's meant to respond with a redirection
    await this.getAdlWithPath(
      { trainingOfferingId, appUserId },
      this.requestTypes.launchTrainingOffering,
      'Launch Training Offering (GET)'
    );
  }
  launchTrainingOfferingUrl(trainingOfferingId: DbKey<DB.TrainingOffering>, appUserId: DbKey<DB.AppUser>): string {
    // This does not use a query string - the URL contains the payload (/training-offering/launch/:trainingOfferingId/:appUserId)
    // Does not actually call the API, instead it returns the URL. For use in Markdown contexts and other places where
    // a DOM link is directly clicked.
    // No auth headers provided
    return this.getPath({ trainingOfferingId, appUserId }, this.requestTypes.launchTrainingOffering);
  }

  async logStartTrainingOffering(
    trainingOfferingId: DbKey<DB.TrainingOffering>,
    appUserId: DbKey<DB.AppUser>
  ): Promise<R.LaunchTrainingOfferingResp> {
    return this.postAdl(
      { trainingOfferingId, appUserId },
      this.requestTypes.launchTrainingOffering,
      'Launch Training Offering (POST)'
    );
  }

  // TODO Re-document this
  async getCourseInfoWithTrackedLink(
    trainingOfferingId: DbKey<DB.TrainingOffering>,
    appUserId: DbKey<DB.AppUser>
  ): Promise<R.TrackedLinkResp> {
    return this.postAdl(
      { trainingOfferingId, appUserId },
      this.requestTypes.trackedLink,
      'Get tracked link (one-time URL) for Training Offering'
    );
  }

  async getCompletionEvent(
    trainingOfferingId: DbKey<DB.TrainingOffering>,
    appUserId: DbKey<DB.AppUser>
  ): Promise<LE.CompletionEvent | null> {
    const response = await this.postAdl(
      { trainingOfferingId, appUserId },
      this.requestTypes.getCompletionEvent,
      'Get completion event for learning offering from event log'
    );
    return response.event;
  }

  /// This is a GET request with no request parameters whatsoever - only a response
  private async getAdl<O>(requestType: RT.GetRequest<O>, actionName: string): Promise<O> {
    return this.requestAdl('get', requestType.path, null, requestType.respJB, actionName);
  }

  /// This is a GET request with a payload (path) - effectively a POST but without
  /// a request body - NB it is meant to be idempotent
  /// In the ADL, define as a POST, but implement through this call in Service
  private async getAdlWithPath<I, O>(
    // Simulate POST
    req: I,
    requestType: RT.PostRequest<I, O>,
    actionName: string
  ): Promise<O> {
    const jsonArgs = requestType.reqJB.toJson(req);
    const pathArgs = jsonArgs !== null ? Object.values(jsonArgs).join('/') : '';
    return this.requestAdl('get', requestType.path + '/' + pathArgs, null, requestType.respJB, actionName);
  }

  /// This is a GET request with a payload (query string) - effectively a POST but without
  /// a request body - NB it is meant to be idempotent
  /// Potentially the 'body' could be encoded in the PATH (= getAdl), but this handles query strings.
  /// TODO requestType.reqJB should be a StringMap:
  ///   { 'name1': val1, 'name2': val2, ... } -> name1=val1&name2=val2...
  /// or
  ///   [ ['name1', val1], ['name2', val2], ... ] -> name1=val1&name2=val2...
  /// In the ADL, define as a POST, but implement through this call in Service
  private async getAdlWithPayload<I, O>(
    // Simulate POST
    req: I,
    requestType: RT.PostRequest<I, O>,
    actionName: string
  ): Promise<O> {
    const jsonArgs = requestType.reqJB.toJson(req);
    return this.requestAdl('get', requestType.path, jsonArgs, requestType.respJB, actionName);
  }

  // Derive the URL which will be sent by the service. This is for use in locations such as Markdown documents
  // This is really only useful for (a) GET requests and (b) requests which don't require any level of authorization.
  private getPath<I, O>(req: I, requestType: RT.PostRequest<I, O>): string {
    const jsonArgs = requestType.reqJB.toJson(req);
    const pathArgs = jsonArgs !== null ? Object.values(jsonArgs).join('/') : '';
    return this.baseUrl + requestType.path + '/' + pathArgs;
  }
  private getPathAndSearch<I, O>(req: I, requestType: RT.PostRequest<I, O>): string {
    const jsonArgs = requestType.reqJB.toJson(req);
    const search = jsonArgs !== null ? new URLSearchParams(jsonArgs) : undefined;
    const searchString = search !== undefined ? '?' + search.toString() : '';
    return this.baseUrl + requestType.path + searchString;
  }

  private async postAdl<I, O>(req: I, requestType: RT.PostRequest<I, O>, actionName: string): Promise<O> {
    const jsonArgs = requestType.reqJB.toJson(req);
    return this.requestAdl('post', requestType.path, jsonArgs, requestType.respJB, actionName);
  }

  private async requestAdl<O>(
    method: 'get' | 'post',
    path: string,
    jsonArgs: {} | null,
    respJB: JsonBinding<O>,
    /** Publicly consumable action of the request for error alerting purposes */
    actionName: string
  ): Promise<O> {
    // Construct request
    const authToken = this.tokenManager.getToken();
    // tslint:disable-next-line: no-console
    // console.debug(`requestAdl ${path} ${actionName}: token: `, authToken);

    const headers: { [key: string]: string } = {};
    if (authToken) {
      headers['X-Auth-Token'] = authToken;
    }

    const httpReq: HttpRequest = {
      url: this.baseUrl + path,
      headers,
      method,
      body: jsonArgs && method === 'post' ? JSON.stringify(jsonArgs) : undefined,
      search: jsonArgs && method === 'get' ? new URLSearchParams(jsonArgs) : undefined
    };

    // Make request
    const resp = await this.http.fetch(httpReq);

    // Check for errors
    if (!resp.ok) {
      const bodyText = await resp.text();
      let publicMessageFragment = '';
      try {
        const bodyJson = JSON.parse(bodyText);
        if (bodyJson.publicMessage) {
          publicMessageFragment = `: ${bodyJson.publicMessage}`;
        }
      } catch (e) {
        // Not JSON
      }

      const error = new ServiceError(
        `Encountered server error attempting to ${actionName} ${publicMessageFragment}`,
        `${httpReq.method} request to ${httpReq.url} failed: ${resp.statusText} (${resp.status}): ${bodyText}`,
        resp.status
      );
      this.errorHandler(error);
      throw error;
    }

    // Parse response
    try {
      const respJson = await resp.json();
      return respJB.fromJson(respJson);
    } catch (e) {
      const error = new ServiceError(
        'Encountered parse error attempting to ' + actionName,
        'parse response',
        resp.status
      );
      this.errorHandler(error);
      throw error;
    }
  }

}



// WEBPACK FOOTER //
// ./src/service/service.ts